import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import JSZip from "jszip";
import { saveAs } from "file-saver";

function ConvertImages() {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [outputFormat, setOutputFormat] = useState("png");
  const [isLoading, setIsLoading] = useState(false); // Adicionado estado de loading
  const { t } = useTranslation();

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
  });

  const handleFormatChange = (event) => {
    setOutputFormat(event.target.value);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("format", outputFormat);

    try {
      const response = await fetch("https://api.pandin.dev/api/convert-image", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Erro ao enviar a imagem.");
      }

      const { download_url } = await response.json();

      // Substituir o caminho do arquivo pelo novo endpoint
      const serveFileUrl = `https://api.pandin.dev/api/serve-file/${
        download_url.split("/storage/")[1]
      }`;

      // Fetch the image immediately after receiving the download URL
      const imageResponse = await fetch(serveFileUrl);
      if (!imageResponse.ok) {
        throw new Error("Erro ao coletar a imagem convertida.");
      }

      const blob = await imageResponse.blob();
      return { blob, download_url };
    } catch (error) {
      console.error(error);
      alert("Erro ao enviar ou coletar a imagem.");
      return null;
    }
  };

  const handleDownloadAsZip = async (urls) => {
    const zip = new JSZip();

    for (const { blob, download_url } of urls) {
      try {
        // Extrair o nome original do arquivo a partir do URL
        const fileName = download_url.split("/").pop();
        zip.file(fileName, blob);
      } catch (error) {
        console.error(error);
        alert("Erro ao baixar uma ou mais imagens.");
        return;
      }
    }

    const now = new Date();
    const formattedDate = `_${now.getHours()}:${now.getMinutes()}_${now.getDate()}:${
      now.getMonth() + 1
    }:${now.getFullYear()}`;

    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, `converted_images${formattedDate}.zip`);
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (selectedFiles.length === 0) {
      alert("Por favor, selecione pelo menos um arquivo.");
      return;
    }
    setIsLoading(true); // Inicia o loading
    try {
      const downloadUrls = await Promise.all(
        selectedFiles.map((file) => handleUpload(file))
      );
      const validUrls = downloadUrls.filter((url) => url !== null);
      if (validUrls.length === 1) {
        const blob = validUrls[0].blob;
        const fileName = `converted_image.${outputFormat}`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else if (validUrls.length > 1) {
        await handleDownloadAsZip(validUrls);
      }
    } finally {
      setIsLoading(false); // Finaliza o loading
    }
  };

  return (
    <div className="container-fluid py-5 mt-5 convert-images">
      <div className="container">
        <div className="row">
          <div className="col-12 d-flex flex-column justify-content-center first-section">
            <div className="col-12 d-flex justify-content-center title-container">
              <div className="title-row d-flex align-items-center justify-content-center">
                <div className="title">{t("ConvertImages.Title")}</div>
                <div className="animation-container-title"></div>
              </div>
            </div>
            <div className="col-12 text-center text-white-70 mt-2 mb-4 fs-5">
                {t("ConvertImages.Subtitle")}
              </div>
          </div>
        </div>
        <div
          {...getRootProps({
            className:
              "dropzone border border-primary rounded p-4 text-center position-relative",
            onClick: (event) => {
              if (
                event.target.tagName === "BUTTON" ||
                event.target.closest(".preview-item")
              ) {
                event.stopPropagation();
              }
            },
          })}
          style={{ cursor: "pointer" }}
        >
          <input {...getInputProps()} />
          <p>{t("ConvertImages.DropzoneText")}</p>
          <div className="dropzone-previews mt-3">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="preview-item d-inline-block me-2 position-relative"
              >
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="img-thumbnail"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    cursor: "default",
                  }}
                />
                <button
                  type="button"
                  className="btn btn-danger btn-sm position-absolute top-0 end-0"
                  style={{
                    backgroundColor: "red",
                    color: "white",
                    fontSize: "25px",
                    borderRadius: "50%",
                    width: "25px",
                    height: "25px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: "translate(5px, -5px)",
                  }}
                  onClick={() => handleRemoveFile(index)}
                  aria-label="Remove"
                >
                  <span
                    style={{
                      transform: "translateY(-3px)",
                    }}
                  >
                    &times;
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>
        <form
          onSubmit={handleSubmit}
          className="row mt-3 g-3 justify-content-center"
        >
          {!isLoading && (
            <div className="col-md-6">
              <label htmlFor="format" className="form-label">
                {t("ConvertImages.OutputFormatLabel")}
              </label>
              <select
                id="format"
                className="form-select"
                value={outputFormat}
                onChange={handleFormatChange}
              >
                <option value="png">PNG</option>
                <option value="jpg">JPG</option>
                <option value="webp">WEBP</option>
                <option value="jpeg">JPEG</option>
              </select>
            </div>
          )}
          <div className="col-12 text-center">
            {!isLoading ? (
              <button type="submit" className="btn btn-primary">
                {t("ConvertImages.SubmitButton")}
              </button>
            ) : (
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Convertendo...</span>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ConvertImages;
